/**
 * Declarative secondary motion system.
 *
 * Defines data-driven rules where one bone's rotation automatically
 * influences another. Rules are evaluated after primary FK/IK solving
 * and constraints, producing additional bone poses that are merged
 * into the store.
 *
 * Example: scapulohumeral rhythm — when the upper arm elevates past
 * 60°, the clavicle auto-rotates at 1:3 ratio.
 */

import * as THREE from "three/webgpu";
import { getRestPose } from "./boneRegistry.ts";
import { applyConstraint } from "./constraints.ts";

// ---------------------------------------------------------------------------
// Rule definition
// ---------------------------------------------------------------------------

export interface FollowRule {
  /** Bone whose rotation triggers this rule */
  driver: string;
  /** Bone that auto-rotates in response */
  follower: string;
  /** Radians of driver rotation (from rest) before effect kicks in */
  threshold: number;
  /** Follower rotates this fraction of the driver's excess rotation */
  ratio: number;
}

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

const deg = (d: number) => (d * Math.PI) / 180;

export const FOLLOW_RULES: FollowRule[] = [
  // Scapulohumeral rhythm: clavicle follows arm elevation (~1:3 ratio).
  // In real anatomy the scapula rotates ~1° for every 2° of arm elevation
  // past the first 60°. We approximate with a 1:3 ratio.
  {
    driver: "upper_armL",
    follower: "shoulderL",
    threshold: deg(60),
    ratio: 0.33,
  },
  {
    driver: "upper_armR",
    follower: "shoulderR",
    threshold: deg(60),
    ratio: 0.33,
  },

  // Lumbopelvic rhythm: pelvis tilts to accommodate extreme hip flexion.
  // When a thigh elevates past ~45°, the pelvis tilts anteriorly at ~1:4 ratio.
  // This is what allows a high kick / grand battement to exceed 90°.
  { driver: "thighL", follower: "hips", threshold: deg(45), ratio: 0.25 },
  { driver: "thighR", follower: "hips", threshold: deg(45), ratio: 0.25 },

  // Thoracolumbar coupling: upper spine compensates for extreme arm reach.
  // When reaching far overhead, the thoracic spine extends slightly (~1:5).
  { driver: "upper_armL", follower: "spine1", threshold: deg(100), ratio: 0.2 },
  { driver: "upper_armR", follower: "spine1", threshold: deg(100), ratio: 0.2 },
];

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

const _identity = new THREE.Quaternion();
const _driverRest = new THREE.Quaternion();
const _driverPose = new THREE.Quaternion();
const _delta = new THREE.Quaternion();
const _scaled = new THREE.Quaternion();
const _followerRest = new THREE.Quaternion();
const _accumulated = new THREE.Quaternion();
const _prevResult = new THREE.Quaternion();

/**
 * Evaluate all follow rules and return additional bone poses.
 *
 * @param objectId - Mannequin object ID
 * @param directlySetBones - Bones being explicitly set by the user (FK/IK).
 *                           Followers in this set are skipped (user wins).
 * @param poses - Current bone poses (including just-solved ones)
 * @returns Map of follower bone names → quaternion tuples to merge into poses
 */
export function applySecondaryMotion(
  objectId: string,
  directlySetBones: Set<string>,
  poses: Map<string, [number, number, number, number]>,
  constraintWeight: number = 1,
): Map<string, [number, number, number, number]> {
  const results = new Map<string, [number, number, number, number]>();

  for (const rule of FOLLOW_RULES) {
    // Skip if follower is being directly manipulated
    if (directlySetBones.has(rule.follower)) continue;

    // Get driver's current pose and rest pose
    const driverPoseArr = poses.get(rule.driver);
    if (!driverPoseArr) continue;
    const driverRestArr = getRestPose(objectId, rule.driver);
    if (!driverRestArr) continue;

    _driverRest.set(
      driverRestArr[0],
      driverRestArr[1],
      driverRestArr[2],
      driverRestArr[3],
    );
    _driverPose.set(
      driverPoseArr[0],
      driverPoseArr[1],
      driverPoseArr[2],
      driverPoseArr[3],
    );

    // Compute driver's delta from rest
    _delta.copy(_driverRest).invert().multiply(_driverPose);
    _delta.normalize();

    // Measure rotation angle
    // Quaternion angle: 2 * acos(|w|)
    const angle = 2 * Math.acos(Math.min(1, Math.abs(_delta.w)));
    if (angle <= rule.threshold) continue;

    // Scale factor: map excess rotation by ratio
    const excess = angle - rule.threshold;
    const scaleFactor = (excess * rule.ratio) / angle;

    // Slerp from identity to delta by scaleFactor
    _scaled.copy(_identity).slerp(_delta, scaleFactor);

    // Apply to follower's rest pose (or accumulate with previous result
    // when multiple drivers target the same follower, e.g., both thighs → hips)
    const followerRestArr = getRestPose(objectId, rule.follower);
    if (!followerRestArr) continue;
    _followerRest.set(
      followerRestArr[0],
      followerRestArr[1],
      followerRestArr[2],
      followerRestArr[3],
    );

    const existing = results.get(rule.follower);
    if (existing) {
      // Accumulate: blend the new delta on top of the previous result.
      // Use the previous result as base instead of rest.
      _prevResult.set(existing[0], existing[1], existing[2], existing[3]);
      _accumulated.copy(_prevResult).multiply(_scaled);
      _accumulated.normalize();
    } else {
      _accumulated.copy(_followerRest).multiply(_scaled);
      _accumulated.normalize();
    }

    // Constrain
    const constrained = applyConstraint(
      rule.follower,
      _accumulated,
      followerRestArr,
      constraintWeight,
    );

    results.set(rule.follower, [
      constrained.x,
      constrained.y,
      constrained.z,
      constrained.w,
    ]);
  }

  return results;
}
